<template>
  <section class="section">
    <div class="column is-half container">
      <b-field expanded>
        <b-input
          expanded
          :disabled="shouldDisplayLoading"
          :loading="shouldDisplayLoading"
          v-model="searchText"
          icon-pack="fas"
          icon="search"
          :placeholder="$t('Search')"
          data-test-id="sectionsSearch"
        />
      </b-field>
    </div>
    <div class="container">
      <sectionsDetails
        :sectionsList="filteredSectionsList"
        :loading="shouldDisplayLoading"
        :canEdit="canCreateShops"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAppStore, type Shop } from '@/stores/app'
import shopsService from '@/services/shopsService'
import sectionsDetails from '@/components/sectionsDetails.vue'

// Types for service compatibility
interface ServiceSection {
  sectionId: number
  sectionName: string
  sectionExternalId?: string
  shoppingCenterGroupId?: number
  shoppingCenterGroupName?: string
}

interface ServiceShoppingCenter {
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  shoppingCenterGroupId?: number
}

interface ServiceShop {
  shopId: number
  shopName: string
  chainId: number
  chainName?: string
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  sections: ServiceSection[]
  creationDate: Date | string
  deactivationDate: Date | string | null
  chainShopId: string | null
  shoppingCenterShopId: string
  groupsRelated?: { groupId: number }[]
  membersRelated?: { memberId: number }[]
  canEditShop: boolean
}

// Composables
const store = useAppStore()

// Reactive state
const searchText = ref<string>('')

// Computed properties
const sectionsList = computed(() => store.getSections)
const shopsList = computed(() => store.getAllowedShopsList)

const shouldDisplayLoading = computed(() => !sectionsList.value)

const shoppingCenterGroupList = computed(() => {
  if (!shopsList.value) return []
  
  // Use Set for uniqueness tracking with proper typing
  const seenGroups = new Set<string>()
  const uniqueShops: Shop[] = []
  
  for (const shop of shopsList.value) {
    const groupName = shop.shoppingCenterGroupName || ''
    if (!seenGroups.has(groupName)) {
      seenGroups.add(groupName)
      uniqueShops.push(shop)
    }
  }
  
  return uniqueShops
})

const sectionsListDetails = computed(() => {
  if (!sectionsList.value) return []
  
  // Convert Pinia store types to service types
  const convertedSections: ServiceSection[] = sectionsList.value.map(section => ({
    sectionId: section.sectionId,
    sectionName: section.sectionName || '',
    sectionExternalId: section.sectionExternalId || '',
    shoppingCenterGroupId: section.shoppingCenterGroupId,
    shoppingCenterGroupName: section.shoppingCenterGroupName || '',
  }))
  
  const convertedShops: ServiceShoppingCenter[] = shoppingCenterGroupList.value.map((shop) => ({
    shoppingCenterId: shop.shoppingCenterId || 0,
    shoppingCenterName: shop.shoppingCenterName || '',
    shoppingCenterGroupName: shop.shoppingCenterGroupName || '',
    shoppingCenterGroupId: shop.shoppingCenterId, // using shoppingCenterId as fallback
  }))
  
  return shopsService.prepareSectionDetails(
    convertedSections,
    convertedShops
  )
})

const filteredSectionsList = computed(() => {
  if (!sectionsListDetails.value.length) return []
  
  return sectionsListDetails.value.filter((section) => {
    const searchTerms = searchText.value
      .toUpperCase()
      .split(/[ ]+/u)
      .filter((el) => el)
    
    if (!searchTerms.length) return true
    
    const searchableText = [
      section.sectionName || '',
      section.sectionId?.toString() || '',
      section.sectionExternalId || '',
      section.shoppingCenterGroupName || '',
    ].join('').toUpperCase()
    
    return searchTerms.every((term) => searchableText.includes(term))
  })
})

const canCreateShops = computed(() => {
  if (!shopsList.value) return false
  
  // Convert Pinia store types to service types with proper section conversion
  const convertedShops: ServiceShop[] = shopsList.value.map((shop) => ({
    shopId: shop.shopId,
    shopName: shop.shopName || '',
    chainId: shop.chainId || 0,
    chainName: shop.chainName,
    shoppingCenterId: shop.shoppingCenterId || 0,
    shoppingCenterName: shop.shoppingCenterName || '',
    shoppingCenterGroupName: shop.shoppingCenterGroupName || '',
    sections: (shop.sections || []).map((section) => ({
      sectionId: section.sectionId,
      sectionName: section.sectionName || '',
      sectionExternalId: section.sectionExternalId || '',
    })),
    creationDate: shop.creationDate,
    deactivationDate: shop.deactivationDate,
    chainShopId: shop.chainShopId || null,
    shoppingCenterShopId: shop.shoppingCenterShopId || '',
    groupsRelated: shop.groupsRelated,
    membersRelated: shop.membersRelated,
    canEditShop: shop.canEditShop || false,
  }))
  
  return shopsService.canCreateShops(convertedShops)
})

// Lifecycle hooks
onMounted(async () => {
  try {
    if (!shopsList.value) {
      await shopsService.getAllowedShopsList()
    }
    if (!sectionsList.value) {
      await shopsService.getSections()
    }
  } catch (error) {
    console.error('Error loading data:', error)
  }
})
</script>